/**
 * Where a candidate sits on the five-stage rail.
 *
 * The one piece of judgement in this screen, so it lives on its own and is tested on
 * its own rather than being buried in a component.
 */

import type { CandidateRead } from '@/api/schema';

/** The five pipeline stages, in order. */
export const STAGES = [
  'Applied',
  'Flyst screening',
  'Approved',
  'RSR screening',
  'Onboarding',
] as const;

/**
 * Which stage each status sits at.
 *
 * Hold and rejected are not stages. They are something that happened *at* a stage, so
 * they map to nothing here and the position is worked out from the screening fields
 * instead. The prototype pins both to the second stage for everybody, which
 * contradicts its own banner ("how far they got before the decision") for anyone
 * stopped after their RSR screening.
 */
const STAGE_OF: Record<CandidateRead['status'], number | null> = {
  candidate_applied: 0,
  details_mailed: 0,
  job_details_sent: 0,
  applicant_screening_arranged: 1,
  applicant_screening_done: 1,
  documentation_started: 1,
  documentation_done: 1,
  approved_for_processing: 2,
  rsr_screening_arranged: 3,
  onboarding_in_progress: 4,
  hold: null,
  rejected: null,
};

export type RailPosition = {
  /** Index into STAGES. For a stopped record, the furthest stage actually reached. */
  index: number;
  /** Null while the candidate is still moving. */
  stopped: 'hold' | 'rejected' | null;
};

type ScreeningFields = Pick<CandidateRead, 'flyst_stage' | 'rsr_state'>;

/**
 * How far a stopped candidate got.
 *
 * Read off the screening fields, which are the only trace this phase has of the route
 * taken. The history table arrives in Phase 16 and will answer it exactly; until then
 * this is a reading of the evidence rather than a record of what happened.
 *
 * Every candidate starts at the introduction stage, so the first screening stage is
 * the floor, which is what the prototype hard-coded for everyone.
 */
function reachedStage({ flyst_stage: flyst, rsr_state: rsr }: ScreeningFields): number {
  if (rsr === 'done' || rsr === 'arranged' || rsr === 're_screening') return 3;
  if (flyst === 'sponsor_screening_arranged') return 3;
  if (flyst === 'ok_for_processing') return 2;
  return 1;
}

export function railPosition(
  candidate: Pick<CandidateRead, 'status' | 'flyst_stage' | 'rsr_state'>,
): RailPosition {
  const stage = STAGE_OF[candidate.status];
  if (stage !== null) return { index: stage, stopped: null };

  // `rsr_state` is the server's derived value, not the stored column: a screening
  // whose date has passed with nothing recorded reads as pending again.
  return {
    index: reachedStage(candidate),
    stopped: candidate.status === 'hold' ? 'hold' : 'rejected',
  };
}
